import { useTableRecordsUtils } from "@/block-system/blocks/Table/Block/TableData";
import {
  getTableContainerElementId,
  getTableElementId,
} from "@/block-system/blocks/Table/Block/TanstackTable";
import { Button } from "@/block-system/brickz/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/block-system/brickz/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/block-system/brickz/components/ui/dropdown-menu";
import { FieldError } from "@/block-system/brickz/components/ui/Field";
import { cn } from "@/block-system/brickz/lib/utils";
import { toast } from "@/components/Toaster/Toaster";
import { usePageContext } from "@/lib/context/page-context";
import { useTrackingContext } from "@/observability";
import { trpc } from "@/utils/trpc";
import { TableRecordForm } from "block-system/components/TableRecordForm";
import { MoreHorizontal, PlusIcon } from "lucide-react";
import { Fragment, useCallback, useRef, useState } from "react";
import { APIRecord } from "server/services/tables/types/records";
import type { APIFieldLatest } from "server/services/tables/types/tables-latest";
import { useInvalidateBlocksUsingTable } from "@/block-system/blocks/Table/lib/invalidateTableRecordQueries";

type UpdateTableRecordFormData = Record<string, unknown>;

type CreateTableRecordFormData = Record<string, unknown>;

export function TableRowActionsMenu({
  canDeleteRecords,
  canEditRecords,
  tableId,
  tableBlockId,
  fields,
  record,
}: {
  canDeleteRecords: boolean;
  canEditRecords: boolean;
  tableId: string;
  tableBlockId: string;
  fields: APIFieldLatest[];
  record: APIRecord;
}) {
  const [editModalVisible, setEditModalVisible] = useState(false);
  const editActionItem = {
    onClick: () => {
      setEditModalVisible(true);
      return true;
    },
    title: "Edit",
    color: "primary",
  };

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const deleteActionItem = {
    onClick: () => {
      setDeleteModalVisible(true);
      return true;
    },
    title: "Delete",
    color: "danger",
  };

  const allFieldsLocked = fields.every((field) => {
    return field.config?.locked === true;
  });

  const menuItems = [
    canEditRecords && !allFieldsLocked ? editActionItem : null,
    canDeleteRecords ? deleteActionItem : null,
  ].filter((action) => action != null);

  const [menuOpen, setMenuOpen] = useState(false);

  const { projectId, pageId, isEmbedded } = usePageContext();
  const hasProjectData =
    projectId != null && pageId != null && isEmbedded != null;

  const renderEditModal = editModalVisible && hasProjectData;
  const renderDeleteModal = deleteModalVisible && hasProjectData;

  /**
   * As per Figma.
   */
  const iconSize = 16;
  const dropdownMenuContainerRef = useRef<HTMLDivElement>(null);

  return (
    <div data-row-actions-open={menuOpen} ref={dropdownMenuContainerRef}>
      <DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button
            className={
              "p-3 text-card-foreground dark:text-card-foreground-subtle"
            }
          >
            <span className="sr-only">Row actions</span>
            <MoreHorizontal
              color={"currentColor"}
              size={iconSize}
              aria-hidden={true}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          /**
           * We have to use custom container to ensure the menu:
           * 1. Disappears when the user scrolls the row outside of the viewport.
           * 2. The menu floats above the table when the user scrolls the row.
           * 3. Works with container queries. See https://github.com/radix-ui/primitives/issues/3143
           */
          container={document.querySelector(
            `#${getTableContainerElementId({ tableId })}`
          )}
          collisionBoundary={document.querySelector(
            `#${getTableElementId({ tableId })}`
          )}
          hideWhenDetached={true}
          avoidCollisions={true}
          /**
           * We are adding the offsets to move the menu popover closer to the trigger.
           */
          sideOffset={-(iconSize / 2)}
          alignOffset={iconSize / 2}
        >
          {menuItems.map((item) => {
            return (
              <DropdownMenuItem
                key={item.title}
                onClick={() => item.onClick()}
                className={cn({
                  "hover:bg-destructive hover:text-destructive-foreground":
                    item.color === "danger",
                  "hover:bg-accent hover:text-accent-foreground":
                    item.color === "primary",
                })}
              >
                {item.title}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {renderEditModal ? (
        <EditTableRecordModal
          fields={fields}
          record={record}
          onClosed={() => setEditModalVisible(false)}
          tableId={tableId}
          tableBlockId={tableBlockId}
          projectId={projectId}
          pageId={pageId}
          isEmbedded={isEmbedded}
        />
      ) : null}
      {renderDeleteModal ? (
        <DeleteTableRecordModal
          onClosed={() => setDeleteModalVisible(false)}
          projectId={projectId}
          pageId={pageId}
          isEmbedded={isEmbedded}
          recordId={record.id}
          tableId={tableId}
          tableBlockId={tableBlockId}
        />
      ) : null}
    </div>
  );
}

export function CreateTableRecord({
  tableBlockId,
  fields,
  projectId,
  pageId,
  isEmbedded,
  tableId,
  newRecordButtonCopy = "New record",
  className,
}: {
  tableBlockId: string;
  fields: APIFieldLatest[];
  projectId: string;
  pageId: string;
  isEmbedded: boolean;
  tableId: string;
  newRecordButtonCopy?: string;
  className?: string;
}) {
  const [creatingRecord, setCreatingRecord] = useState(false);

  return (
    <Fragment>
      <Button onClick={() => setCreatingRecord(true)} className={className}>
        <PlusIcon className="mr-2 inline-block h-4 w-4" aria-hidden={true} />
        <span>{newRecordButtonCopy}</span>
      </Button>
      {creatingRecord ? (
        <CreateTableRecordModal
          tableBlockId={tableBlockId}
          newRecordButtonCopy={newRecordButtonCopy}
          fields={fields}
          onClosed={() => {
            setCreatingRecord(false);
          }}
          tableId={tableId}
          projectId={projectId}
          pageId={pageId}
          isEmbedded={isEmbedded}
        />
      ) : null}
    </Fragment>
  );
}

function CreateTableRecordModal({
  newRecordButtonCopy,
  fields,
  onClosed,
  tableId,
  projectId,
  pageId,
  isEmbedded,
  tableBlockId,
}: {
  newRecordButtonCopy: string;
  fields: APIFieldLatest[];
  onClosed: () => void;
  tableId: string;
  projectId: string;
  pageId: string;
  isEmbedded: boolean;
  tableBlockId: string;
}) {
  const { createTableRecord, isCreatingRecord, isCreateRecordError } =
    useCreateTableRecord({
      projectId,
      pageId,
      isEmbedded,
      onRecordCreated: onClosed,
    });

  const showErrorMessage = !isCreatingRecord && isCreateRecordError;
  const errorMessage = showErrorMessage
    ? "Failed to create new record. Please try again."
    : undefined;

  return (
    <Dialog onOpenChange={onClosed} defaultOpen={true}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{newRecordButtonCopy}</DialogTitle>
        </DialogHeader>
        <DialogDescription className="sr-only">
          {newRecordButtonCopy}
        </DialogDescription>

        <TableRecordForm
          fields={fields}
          record={undefined}
          onSubmit={(data) => {
            createTableRecord({
              tableId,
              data,
              tableBlockId,
            });
          }}
          isSubmitting={isCreatingRecord}
          onCancel={onClosed}
          tableId={tableId}
        />
        <FormErrorMessage
          message={errorMessage}
          testId="create-table-record-modal-error-message"
        />
      </DialogContent>
    </Dialog>
  );
}

function useCreateTableRecord({
  projectId,
  pageId,
  isEmbedded,
  onRecordCreated,
}: {
  projectId: string;
  pageId: string;
  isEmbedded: boolean;
  onRecordCreated: VoidFunction;
}) {
  const { emitConsumerInteractionEvent } = useTrackingContext();
  const utils = trpc.useUtils();
  const { incrementRecordCountInCache } = useTableRecordsUtils();
  const invalidateBlocksUsingTable = useInvalidateBlocksUsingTable();

  const {
    mutate,
    isPending: isCreatingRecord,
    isError: isCreateRecordError,
  } = trpc.blockTable.createRecord.useMutation({
    async onSuccess(_, variables) {
      toast.success({ message: "Record created successfully." });

      emitConsumerInteractionEvent({
        interaction_goal: "Create table record",
        event_action: "click",
        interfaces_project_id: projectId,
        interfaces_page_id: pageId,
        interfaces_component_type: "datagrid",
        embedded_flag: isEmbedded,
      });

      incrementRecordCountInCache();

      /**
       * Since users can search for a given record right after they created it,
       * we decided to it would be safer to wait for invalidations to complete
       * to avoid race conditions.
       *
       * A race condition could occur when user searches for a record,
       *  but the invalidate call has not fired yet.
       */
      await Promise.all([
        invalidateBlocksUsingTable(variables.tableId),
        utils.tableRecords.getChoicesByFieldId.invalidate(undefined, {
          exact: false,
          refetchType: "all",
        }),
      ]);

      void utils.blockKanban.get.invalidate();

      onRecordCreated();
    },
    meta: {
      noToast: true,
    },
  });

  const createTableRecord = useCallback(
    ({
      tableId,
      data,
      tableBlockId,
    }: {
      tableId: string;
      data: CreateTableRecordFormData;
      tableBlockId: string;
    }) => {
      mutate({
        tableId,
        body: { new_records: [{ data: data }] },
        tableBlockId,
      });
    },
    [mutate]
  );

  return {
    isCreatingRecord,
    isCreateRecordError,
    createTableRecord,
  };
}

export function EditTableRecordModal({
  fields,
  record,
  onClosed,
  tableId,
  tableBlockId,
  projectId,
  pageId,
  isEmbedded,
}: {
  fields: APIFieldLatest[];
  record: APIRecord;
  onClosed: () => void;
  tableId: string;
  tableBlockId: string;
  projectId: string;
  pageId: string;
  isEmbedded: boolean;
}) {
  const { editTableRecord, isUpdatingRecord, isUpdateRecordError } =
    useEditTableRecord({
      projectId,
      pageId,
      isEmbedded,
      onRecordUpdated: onClosed,
    });

  const showErrorMessage = !isUpdatingRecord && isUpdateRecordError;
  const errorMessage = showErrorMessage
    ? "Failed to update the record. Please try again."
    : undefined;

  return (
    <Dialog onOpenChange={onClosed} defaultOpen={true}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit record</DialogTitle>
        </DialogHeader>
        <DialogDescription className="sr-only">Edit record</DialogDescription>
        <TableRecordForm
          fields={fields}
          record={record}
          onSubmit={(data) => {
            editTableRecord({
              tableId,
              data,
              tableBlockId,
              recordId: record.id,
            });
          }}
          isSubmitting={isUpdatingRecord}
          onCancel={onClosed}
          tableId={tableId}
        />
        <FormErrorMessage
          message={errorMessage}
          testId="edit-table-record-modal-error-message"
        />
      </DialogContent>
    </Dialog>
  );
}

function useEditTableRecord({
  projectId,
  pageId,
  isEmbedded,
  onRecordUpdated,
}: {
  projectId: string;
  pageId: string;
  isEmbedded: boolean;
  onRecordUpdated: VoidFunction;
}) {
  const { emitConsumerInteractionEvent } = useTrackingContext();
  const { updateRecordInCache, getCurrentQueryInput } = useTableRecordsUtils();
  const utils = trpc.useUtils();
  const invalidateBlocksUsingTable = useInvalidateBlocksUsingTable();
  const {
    mutate,
    isPending: isUpdatingRecord,
    isError: isUpdateRecordError,
  } = trpc.blockTable.updateRecord.useMutation({
    async onSuccess({ data: [{ new: recordUpdates }] }, variables) {
      toast.success({ message: "Record updated successfully." });

      emitConsumerInteractionEvent({
        interaction_goal: "Edit table record",
        event_action: "click",
        interfaces_project_id: projectId,
        interfaces_page_id: pageId,
        interfaces_component_type: "datagrid",
        embedded_flag: isEmbedded,
      });

      const currentQueryInput = getCurrentQueryInput();

      const hasSearchQuery = currentQueryInput.query !== "";
      if (hasSearchQuery) {
        /**
         * We have to invalidate when the query is not empty to ensure the UI is in-sync with search results.
         *
         * 1. Search for exact match.
         * 2. Edit that record so it is different than the search query.
         * 3. Without invalidating, the search results include the updated record even if it doesn't match the query.
         */
        await invalidateBlocksUsingTable(currentQueryInput.tableId);
        void utils.blockKanban.get.invalidate();
      } else {
        updateRecordInCache({
          recordId: variables.body.updated_records[0].id,
          newRecord: recordUpdates,
        });

        void utils.blockKanban.get.invalidate();
        void invalidateBlocksUsingTable(variables.tableId);
      }

      void utils.blockKanban.get.invalidate();

      onRecordUpdated();
    },
    meta: {
      noToast: true,
    },
  });

  const editTableRecord = useCallback(
    ({
      tableId,
      data,
      tableBlockId,
      recordId,
    }: {
      tableId: string;
      data: UpdateTableRecordFormData;
      tableBlockId: string;
      recordId: string;
    }) => {
      mutate({
        tableId,
        body: { updated_records: [{ id: recordId, data: data }] },
        tableBlockId,
      });
    },
    [mutate]
  );

  return {
    editTableRecord,
    isUpdatingRecord,
    isUpdateRecordError,
  };
}

export function DeleteTableRecordModal({
  onClosed,
  projectId,
  pageId,
  isEmbedded,
  recordId,
  tableId,
  tableBlockId,
}: {
  onClosed: () => void;
  projectId: string;
  pageId: string;
  isEmbedded: boolean;
  recordId: string;
  tableId: string;
  tableBlockId: string;
}) {
  const { deleteRecord, isDeletingRecord, isDeleteRecordError } =
    useDeleteTableRecord({
      projectId,
      pageId,
      isEmbedded,
      onRecordDeleted: onClosed,
    });

  const showErrorMessage = !isDeletingRecord && isDeleteRecordError;

  const errorMessage = showErrorMessage
    ? "Failed to delete the record. Please try again."
    : undefined;

  return (
    <Dialog onOpenChange={onClosed} defaultOpen={true}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete this record?</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Are you sure you want to delete this record? This action cannot be
          undone.
        </DialogDescription>
        <DialogFooter className="flex items-center justify-start">
          <Button
            variant="secondary"
            onClick={onClosed}
            disabled={isDeletingRecord}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            isLoading={isDeletingRecord}
            onClick={() => {
              deleteRecord({
                recordId,
                tableId,
                tableBlockId,
              });
            }}
            disabled={isDeletingRecord}
          >
            Delete record
          </Button>
        </DialogFooter>
        <FormErrorMessage
          message={errorMessage}
          testId="delete-table-record-modal-error-message"
        />
      </DialogContent>
    </Dialog>
  );
}

function useDeleteTableRecord({
  projectId,
  pageId,
  isEmbedded,
  onRecordDeleted,
}: {
  projectId: string;
  pageId: string;
  isEmbedded: boolean;
  onRecordDeleted: VoidFunction;
}) {
  const { emitConsumerInteractionEvent } = useTrackingContext();

  const { removeRecordFromCache } = useTableRecordsUtils();

  const {
    mutate,
    isPending: isDeletingRecord,
    isError: isDeleteRecordError,
  } = trpc.blockTable.deleteRecord.useMutation({
    onSuccess(_, variables) {
      toast.success({ message: "Record successfully deleted" });

      emitConsumerInteractionEvent({
        interaction_goal: "Delete table record",
        event_action: "click",
        interfaces_project_id: projectId,
        interfaces_page_id: pageId,
        interfaces_component_type: "datagrid",
        embedded_flag: isEmbedded,
      });

      removeRecordFromCache({ recordId: variables.body.record_ids[0] });

      onRecordDeleted();
    },
    meta: {
      noToast: true,
    },
  });

  const deleteRecord = useCallback(
    ({
      recordId,
      tableId,
      tableBlockId,
    }: {
      recordId: string;
      tableId: string;
      tableBlockId: string;
    }) => {
      mutate({
        tableBlockId,
        tableId,
        body: { record_ids: [recordId] },
      });
    },
    [mutate]
  );

  return {
    deleteRecord,
    isDeletingRecord,
    isDeleteRecordError,
  };
}

function FormErrorMessage({
  message,
  testId,
}: {
  message: string | undefined;
  testId: string;
}) {
  /**
   * See "Examples" in https://www.w3.org/TR/WCAG20-TECHS/ARIA19.html
   * "The error container must be present in the DOM on page load for the error message to be spoken by most screen readers."
   */
  return (
    <div
      className={cn("mx-auto", {
        /**
         * This ensures the parent of the error message does not interfere with the layout when there is no error message.
         */
        contents: message == null,
      })}
      role={"alert"}
      aria-atomic={true}
    >
      {message ? <FieldError data-testid={testId}>{message}</FieldError> : null}
    </div>
  );
}
